import { type StateCreator } from 'zustand';
import { SortingOptionId } from '@pkgs/components/enums/enums';
import { getEntries } from '@ev-tech/search-modules-api';

import { SearchMode } from './searchModeSlice';
import { DEFAULT_SORTING_OPTION_ID } from './optionsSlice';
import { usePromptStore } from '../usePromptStore';
import { defaultSearchState, type MiddlewareType, type SearchState, type SearchStore } from '../SearchStoreProvider';

// Extract only the state properties, excluding action functions
export const extractStateFromStore = <T>(store: Record<string, unknown>): T => {
  const entries = getEntries(store).filter(([, value]) => typeof value !== 'function') as [PropertyKey, unknown][];
  return Object.fromEntries(entries) as T;
};

export type ApplyStateParams = {
  state: Partial<SearchState>;
  shouldCallBeforeFiltersChangeCallback?: boolean;
};

type UtilActions = {
  applyState: (params: ApplyStateParams) => void;
  resetState: (partialState?: Partial<SearchState>) => void;
  getStateSnapshot: () => SearchState;
};

export type UtilStore = UtilActions;

export const createUtilSlice: StateCreator<SearchStore, MiddlewareType, [], UtilStore> = (set, get) => ({
  applyState: ({ state, shouldCallBeforeFiltersChangeCallback = true }: ApplyStateParams) => {
    set(
      {
        ...state,
        ...(state.sortingOptionId === SortingOptionId.PREFERENCES &&
          state.searchMode !== SearchMode.AI && { sortingOptionId: DEFAULT_SORTING_OPTION_ID }),
      },
      undefined,
      {
        type: 'applyState',
        shouldCallBeforeFiltersChangeCallback,
      },
    );
  },
  resetState: (partialState?: Partial<SearchState>) => {
    set({ ...(partialState ?? defaultSearchState), page: 1 }, undefined, 'resetState');
    usePromptStore.getState().setPromptValue('');
  },
  getStateSnapshot: () => structuredClone(extractStateFromStore(get())),
});
